/**
 * Список поясов календаря одной строкой: «Europe/Moscow|МСК;Asia/Tokyo» — пояс и, если человек
 * переименовал, своё имя после «|». Первый в списке — основной. Хранится в личных настройках на сервере
 * (`calendarZones`, недавние — `calendarZonesRecent`); правила те же, что в App\Identity\Domain\Preferences.
 */
export interface ZoneEntry {
  readonly zone: string;
  /** Своё имя пояса («Дом», «МСК»); null — имя по умолчанию. */
  readonly name: string | null;
}

/** Сколько колонок поясов помещается в сетку. */
export const MAX_ZONES = 4;
/** Сколько поясов хранится в сервере всего (колонки или недавние). */
export const STORED_ZONES = 6;
export const ZONE_NAME_MAX = 12;

function isZone(zone: string): boolean {
  if (!/^[A-Za-z0-9_+\-/]+$/.test(zone) || (zone !== 'UTC' && !zone.includes('/'))) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function validName(name: string): boolean {
  return name.length > 0 && name.length <= ZONE_NAME_MAX && !/[;|\p{C}]/u.test(name);
}

export function validZoneList(value: string): boolean {
  if (value === '') return true;
  const entries = value.split(';');
  if (entries.length > STORED_ZONES) return false;
  const seen = new Set<string>();
  for (const e of entries) {
    const [zone = '', ...rest] = e.split('|');
    if (rest.length > 1 || !isZone(zone) || seen.has(zone)) return false;
    if (rest.length === 1 && !validName(rest[0] ?? '')) return false;
    seen.add(zone);
  }
  return true;
}

export function parseZones(value: string): readonly ZoneEntry[] {
  if (!validZoneList(value) || value === '') return [];
  return value.split(';').map((e) => {
    const [zone = '', name] = e.split('|');
    return { zone, name: name ?? null };
  });
}

export function serializeZones(list: readonly ZoneEntry[]): string {
  return list.map((e) => (e.name === null ? e.zone : `${e.zone}|${e.name}`)).join(';');
}

/** Недавние: выбранный пояс — наверх, без повторов, не больше хранимого. */
export function pushRecent(recent: string, zone: string): string {
  const list = parseZones(recent).filter((e) => e.zone !== zone);
  return serializeZones([{ zone, name: null }, ...list].slice(0, STORED_ZONES));
}
